import React, { useState, useEffect , useRef} from 'react';
import './Dashboard.css';
import { getZonesOccupancy, getPublicFacilities } from '../../services/camera.service';
import { checkAdminStatus } from '../../services/admin.service';
import AdminPanel from '../AdminPanel/AdminPanel';

const BASE_FACILITIES = [
  { id: 1, name: 'Rush Rhees Library', current: 120, capacity: 200, status: 'HIGH', type: 'library' },
  { id: 2, name: 'iZone', current: 45, capacity: 150, status: 'MODERATE', type: 'library' },
  { id: 3, name: 'Arts & Music Library', current: 25, capacity: 100, status: 'LOW', type: 'library' },
  { id: 4, name: 'Carlson Library', current: 85, capacity: 180, status: 'HIGH', type: 'library' },
  { id: 5, name: 'Douglass Dining Center', current: 120, capacity: 200, status: 'HIGH', type: 'dining' },
  { id: 6, name: 'Danforth Dining Center', current: 65, capacity: 150, status: 'MODERATE', type: 'dining' },
  { id: 7, name: 'Goergen Athletic Center', current: 25, capacity: 100, status: 'LOW', type: 'gym' },
];

const Dashboard = () => {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [filter, setFilter] = useState('All');
  const [hoveredBar, setHoveredBar] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isURAffiliate, setIsURAffiliate] = useState(false);
  const [zonesOccupancy, setZonesOccupancy] = useState([]);

  const [facilities, setFacilities] = useState(BASE_FACILITIES);

  const fetchData = async () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    if (!user || !user.access_token) {
      setIsURAffiliate(false);
      setFacilities(BASE_FACILITIES);
      return;
    }

    // Logged-in users: fetch real DB facilities + live zone occupancy
    try {
      const [facilitiesData, occupancy] = await Promise.all([
        getPublicFacilities(),
        getZonesOccupancy(),
      ]);

      setZonesOccupancy(occupancy);

      const dbFacs = facilitiesData.facilities || [];

      // Sum current_count per facility from zone occupancy
      const occupancyByFacility = {};
      occupancy.forEach(z => {
        const key = z.facility_name;
        occupancyByFacility[key] = (occupancyByFacility[key] || 0) + (z.current_count || 0);
      });

      if (dbFacs.length > 0) {
        setIsURAffiliate(true);
        setFacilities(dbFacs.map(f => {
          const current = occupancyByFacility[f.display_name] || 0;
          const capacity = f.max_capacity || 200;
          const ratio = current / capacity;
          const status = ratio > 0.7 ? 'HIGH' : ratio > 0.3 ? 'MODERATE' : 'LOW';
          return { id: `db_${f.id}`, name: f.display_name, current, capacity, status, type: 'facility' };
        }));
      } else {
        setIsURAffiliate(false);
        setFacilities(BASE_FACILITIES);
      }
    } catch {
      setIsURAffiliate(false);
      setFacilities(BASE_FACILITIES);
    }
  };

  const checkAdmin = async () => {
    try {
      // Only check admin status if user is logged in
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (!user?.access_token) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      const adminStatus = await checkAdminStatus();
      setIsAdmin(adminStatus);
    } catch (err) {
      // Silently handle - user is not an admin (no console log needed)
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  useEffect(() => {
    fetchData();
    checkAdmin();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      fetchAnalyticsData(selectedFacility.id);
    }
  }, [selectedFacility]);

  const fetchAnalyticsData = async (facilityId) => {
    // Fetch real data from database for UR affiliates
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.access_token;

      if (token) {
        // DB-sourced facilities have id like "db_3" — extract the numeric part
        const apiId = typeof facilityId === 'string' && facilityId.startsWith('db_')
          ? facilityId.slice(3)
          : facilityId;
        const response = await fetch(`https://campuslens.xyz/api/facilities/${apiId}/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setAnalyticsData(data);
          return;
        }
      }
    } catch (err) {
      // ignore — fall through to sample data for unauthenticated users
    }

    // Only show sample data for users who are not logged in
    const user2 = JSON.parse(localStorage.getItem('user') || 'null');
    if (user2?.access_token) return; // authenticated — no fake data, just no chart

    // Provide sample analytics data for non-authenticated users
    const now = new Date();
    const sampleData = {
      todayOccupancy: Array.from({ length: 24 }, (_, i) => ({
        timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), i, 0).toISOString(),
        count: Math.floor(Math.random() * 50) + 20
      })),
      weeklyAverages: {
        Monday: 145, Tuesday: 160, Wednesday: 175,
        Thursday: 155, Friday: 140, Saturday: 90, Sunday: 85
      },
      peakHour: '2:00 PM',
      quietHour: '8:00 AM',
      busiestDay: 'Wednesday',
      busiestCount: 175,
      quietestDay: 'Sunday',
      quietestCount: 85,
      occupancyTrends: {
        lastHour: 45,
        lastDay: 1250,
        lastWeek: 8500,
        weeklyGrowth: 5.2,
        avgDaily: 178
      },
      facilityHours: 'Sample Data',
      openTime: null,
      closeTime: null
    };
    setAnalyticsData(sampleData);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'HIGH': return '#ff4d4d';
      case 'MODERATE': return '#ffd700';
      case 'LOW': return '#4CAF50';
      default: return '#gray';
    }
  };

  const handleCardClick = (facility) => {
    setSelectedFacility(facility);
    if (isMobile) {
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCloseModal = () => {
    setSelectedFacility(null);
    document.body.style.overflow = 'unset';
  };

  const [filteredFacilities, setFilteredFacilities] = useState([]);

  useEffect(() => {
    if (!facilities) return;
    setFilteredFacilities(filter === 'All'
      ? facilities
      : facilities.filter(f => f.type === filter.toLowerCase()));
  }, [facilities, filter]);

  const renderOccupancyChart = (data) => {
    if (!data?.todayOccupancy) return null;

    // Parse time strings like "6:30 AM" or "11 PM" to 24-hour format
    const parseTime = (timeStr) => {
      if (!timeStr) return null;
      const match = timeStr.match(/(\d+):?(\d+)?\s*(AM|PM)/i);
      if (!match) return null;
      let hour = parseInt(match[1]);
      const minute = match[2] ? parseInt(match[2]) : 0;
      const period = match[3].toUpperCase();

      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;

      return hour * 60 + minute; // Return minutes since midnight
    };

    // Filter occupancy data to only show times within facility hours
    let filteredOccupancy = data.todayOccupancy;
    if (data.openTime && data.closeTime && filteredOccupancy.length > 0) {
      const openMinutes = parseTime(data.openTime);
      const closeMinutes = parseTime(data.closeTime);

      if (openMinutes !== null && closeMinutes !== null) {
        const tempFiltered = data.todayOccupancy.filter(point => {
          // Ensure timestamp is properly parsed as UTC/ISO format
          const date = new Date(point.timestamp);
          const pointMinutes = date.getHours() * 60 + date.getMinutes();
          return pointMinutes >= openMinutes && pointMinutes <= closeMinutes;
        });

        // Only apply filter if it doesn't remove all data
        if (tempFiltered.length > 0) {
          filteredOccupancy = tempFiltered;
        }
      }
    }

    if (filteredOccupancy.length === 0) return null;

    const maxCount = Math.max(...filteredOccupancy.map(d => d.count));

    const getTimeRange = (timestamp) => {
      const date = new Date(timestamp);
      const hour = date.getHours();
      const minute = date.getMinutes();
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    };

    const formatTimeLabel = (timestamp) => {
      const date = new Date(timestamp);
      const hour = date.getHours();
      const minute = date.getMinutes();
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return minute === 0 ? `${displayHour} ${ampm}` : `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };

    // Calculate which indices should show x-axis labels (approximately every 1.5 hours = 90 minutes)
    const getXAxisLabels = () => {
      const labels = [];
      const intervalMinutes = 90; // 1.5 hours

      filteredOccupancy.forEach((point, index) => {
        const date = new Date(point.timestamp);
        const totalMinutes = date.getHours() * 60 + date.getMinutes();

        // Show label if it's at an interval or the first/last point
        if (index === 0 || index === filteredOccupancy.length - 1 || totalMinutes % intervalMinutes === 0) {
          labels.push({ index, label: formatTimeLabel(point.timestamp) });
        }
      });

      return labels;
    };

    const xAxisLabels = getXAxisLabels();

    return (
      <div className="chart-wrapper">
        <div className="chart-axis-y">
          {[...Array(5)].map((_, i) => (
            <span key={i}>{Math.round((maxCount / 4) * (4 - i))}</span>
          ))}
        </div>
        <div className="chart-content">
          <div className="simple-chart">
            {filteredOccupancy.map((point, index) => (
              <div
                key={index}
                className={`chart-bar ${hoveredBar === index ? 'active' : ''}`}
                style={{
                  height: `${(point.count / maxCount) * 100}%`,
                  backgroundColor: `rgba(76, 175, 80, ${(point.count / maxCount) * 0.8 + 0.2})`
                }}
                onMouseEnter={() => setHoveredBar(index)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div className={`chart-tooltip ${hoveredBar === index ? 'visible' : ''}`}>
                  <strong>{point.count} people</strong>
                  <span>{getTimeRange(point.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="chart-grid">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="chart-grid-line" />
            ))}
          </div>
          <div className="chart-axis-x">
            {filteredOccupancy.map((point, index) => {
              const label = xAxisLabels.find(l => l.index === index);
              return (
                <span key={index} className={label ? 'x-label visible' : 'x-label'}>
                  {label ? label.label : ''}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // If showing admin panel, render only that
  if (showAdminPanel) {
    return <AdminPanel />;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-top">
          <h1>Campus Facilities Status</h1>
          {!checkingAdmin && isAdmin && (
            <button
              className="admin-toggle-btn"
              onClick={() => setShowAdminPanel(true)}
              title="Admin Panel"
            >
              <span className="admin-icon">🔑</span>
              Admin Panel
            </button>
          )}
        </div>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'All' ? 'active' : ''}`}
            onClick={() => setFilter('All')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'library' ? 'active' : ''}`}
            onClick={() => setFilter('library')}
          >
            Libraries
          </button>
          <button
            className={`filter-btn ${filter === 'dining' ? 'active' : ''}`}
            onClick={() => setFilter('dining')}
          >
            Dining Halls
          </button>
          <button
            className={`filter-btn ${filter === 'gym' ? 'active' : ''}`}
            onClick={() => setFilter('gym')}
          >
            Gym
          </button>
        </div>
      </div>
      {!isURAffiliate && (
        <div className="sample-data-banner">
          <span className="banner-icon">ℹ️</span>
          <span className="banner-text">This is sample data, not pulling from actual locations</span>
        </div>
      )}
      <div className="facilities-grid">
        {filteredFacilities && filteredFacilities.constructor == Array && filteredFacilities.map(facility => (
          <div
            key={facility.id}
            className="facility-card"
            onClick={() => handleCardClick(facility)}
          >
            <h2>{facility.name}</h2>
            <div className="occupancy-bar">
              <div
                className="occupancy-fill"
                style={{
                  width: `${(facility.current / facility.capacity) * 100}%`,
                  backgroundColor: getStatusColor(facility.status)
                }}
              ></div>
            </div>
            <div className="facility-stats">
              <span>{facility.current} / {facility.capacity}</span>
              <span className={`status-badge ${facility.status.toLowerCase()}`}>
                {facility.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      {selectedFacility && analyticsData && (
        <div className="analytics-modal" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{selectedFacility.name} Analytics</h2>
            <div className="occupancy-chart">
              <h3>Today's Occupancy Trend</h3>
              {renderOccupancyChart(analyticsData)}
            </div>
            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>Current Status</h3>
                <p className="analytics-number">{selectedFacility.current}</p>
                <div className="analytics-details">
                  <p>Capacity: {selectedFacility.capacity}</p>
                  <p>Utilization: {Math.round((selectedFacility.current / selectedFacility.capacity) * 100)}%</p>
                </div>
              </div>
              <div className="analytics-card">
                <h3>Today's Traffic</h3>
                <div className="analytics-details">
                  <p>Peak Time: {analyticsData.peakHour !== 'N/A' ? analyticsData.peakHour : '—'}</p>
                  <p>Quiet Time: {analyticsData.quietHour !== 'N/A' ? analyticsData.quietHour : '—'}</p>
                  <p>Today's Total: {analyticsData.occupancyTrends.lastDay} detections</p>
                  <p>Avg Daily: {analyticsData.occupancyTrends.avgDaily} detections</p>
                </div>
              </div>
              <div className="analytics-card">
                <h3>Weekly Overview</h3>
                <div className="analytics-details">
                  <p>Busiest Day: {analyticsData.busiestDay !== 'N/A' ? `${analyticsData.busiestDay} (avg ${analyticsData.busiestCount})` : '—'}</p>
                  <p>Quietest Day: {analyticsData.quietestDay !== 'N/A' ? `${analyticsData.quietestDay} (avg ${analyticsData.quietestCount})` : '—'}</p>
                  <p>Weekly Growth: {analyticsData.occupancyTrends.weeklyGrowth !== 0 ? `${analyticsData.occupancyTrends.weeklyGrowth > 0 ? '+' : ''}${analyticsData.occupancyTrends.weeklyGrowth}%` : '—'}</p>
                </div>
              </div>
              <div className="analytics-card">
                <h3>Recommendations</h3>
                <div className="analytics-details recommendation-list">
                  {analyticsData.quietHour !== 'N/A' && <p>• Best time to visit: Around {analyticsData.quietHour}</p>}
                  {analyticsData.peakHour !== 'N/A' && <p>• Avoid peak hours: Around {analyticsData.peakHour}</p>}
                  <p>• Current occupancy: {Math.round((selectedFacility.current / selectedFacility.capacity) * 100)}% of capacity</p>
                  {analyticsData.busiestDay !== 'N/A' && <p>• Busiest day this week: {analyticsData.busiestDay}</p>}
                </div>
              </div>
            </div>
            {zonesOccupancy.length > 0 && (
              <div className="zone-breakdown">
                <h3>Zone Breakdown</h3>
                <div className="zone-cards">
                  {zonesOccupancy.map(z => (
                    <div key={z.zone_id} className="zone-card">
                      <div className="zone-card-name">{z.zone_name}</div>
                      <div className="zone-card-count">{z.current_count}</div>
                      <div className="zone-card-label">people</div>
                      {z.facility_name && <div className="zone-card-facility">{z.facility_name}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button className="close-modal" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
